import { getAllPermissionActions, type permissionManifest } from "@msuite/citadel";
import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createMeta } from "@msuite/ui/table";
import { grantKey, localizedAction, localizedSubject } from "@/components/citadel";
import type { RoleSource } from "./permission-form-values";
import { UserPermissionScopeCell } from "./user-permission-scope-cell";

/** One row of the grid: a manifest subject, keyed by itself. */
export type UserPermissionRow = { id: string } & (typeof permissionManifest)[number];

const columnHelper = createColumnHelper<UserPermissionRow>();

/** Props Interface */
interface CreateColumnsOptions {
	/** The roles granting each pair, with their reaches — the badge's tooltip. */
	roleSources: Record<string, RoleSource[]>;
	/** The pairs the user holds under a template the scope map does not know. */
	customKeys: Set<string>;
}

/**
 * The grid's columns: the subject, then one per action the manifest declares anywhere — the same
 * rows, headers, order and scope cells as the role editor, so the two grids read as one table
 * about two subjects. Built per render rather than at module evaluation, so the labels follow a
 * language switch; the static column configs elsewhere in the app do not.
 *
 * There is deliberately no trailing actions column. A row has nothing to act on, and the empty
 * one that used to sit here reserved 400px pinned to the right, which pushed the last action
 * column — `approve` — out of view.
 */
export function createColumns({ roleSources, customKeys }: CreateColumnsOptions) {
	return [
		columnHelper.accessor("subject", {
			id: "subject",
			header: localized("permissions.permission"),
			cell: (cell) => localizedSubject(cell.getValue()),
		}),
		...getAllPermissionActions().map((action) =>
			columnHelper.display({
				id: action,
				header: localizedAction(action),
				// The cell is the select: the pill fills it edge to edge, so the cell padding
				// would only draw a dead margin around the control.
				meta: createMeta({ withoutPadding: true }),
				cell: (cell) => (
					<UserPermissionScopeCell
						item={cell.row.original}
						action={action}
						isCustom={customKeys.has(grantKey(action, cell.row.original.subject))}
						sources={roleSources[grantKey(action, cell.row.original.subject)] ?? []}
					/>
				),
			}),
		),
	];
}
